import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import SequenceList from "./SequenceList";
import SequenceConfig from "./SequenceConfig";
import { isNullOrEmpty } from "../../../utils/general";
import Toaster from "../../../components/Toaster";
import Icon from "react-web-vector-icons";

import "./Reports.scss";
import * as staticData from "../../../redux/staticData/actions";

const Reports = (props) => {
  const { vendor, runReport } = props;
  const dispatch = useDispatch();

  // Get related contacts state from Redux
  const hasRelatedContacts = useSelector(
    (state) => state.mapping.hasRelatedContacts
  );
  const contactRoleCategory = useSelector(
    (state) => state.mapping.contactRoleCategory
  );
  const contactRoleSegment = useSelector(
    (state) => state.mapping.contactRoleSegment
  );
  const contactRole = useSelector((state) => state.mapping.contactRole);

  const uniqeKey = () => Date.now() + Math.floor(Math.random() * 10000);
  const initEmptyRow = () => ({
    key: uniqeKey(),
    field: "",
    type: "",
    value: "",
    checkErrors: false,
  });

  const [queryRows, setQueryRows] = useState([[initEmptyRow()]]); // Initialize with one empty row
  const [dropdownData, setDropdownData] = useState(null);
  const [selectedSequenceIndex, setSelectedSequenceIndex] = useState(0);
  const [showConfig, setShowConfig] = useState(false);
  const [showSequenceList, setShowSequenceList] = useState(true);
  const [showQuery, setShowQuery] = useState(true);
  const quearyStoragekey = vendor + "_queryRows";

  useEffect(() => {
    const saved = localStorage.getItem(quearyStoragekey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setQueryRows(parsed);
      } catch (err) {
        console.error("Failed to parse saved query rows", err);
      }
    }
  }, [vendor]);

  useEffect(() => {
    dispatch(
      staticData.loadStaticData((data) => {
        setDropdownData(data);
      })
    );
  }, []);

  useEffect(() => {
    localStorage.setItem(quearyStoragekey, JSON.stringify(queryRows));
  }, [queryRows]);

  const onChangeQueryRow = (updatedRow) => {
    console.log("updatedRow", updatedRow);
    const newRows = queryRows.map((group) =>
      group.map(
        (row) => (row.key === updatedRow.key ? { ...row, ...updatedRow } : row) // Spread to create a new object
      )
    );
    setQueryRows(newRows);
  };

  const onRunReport = () => {
    let hasValidRow = false;

    const cleaned = queryRows
      .map((group) =>
        group
          .map((row) => ({ ...row, checkErrors: false }))
          .filter((row) => {
            // accept arrays from multi-select and non-empty strings
            const valueValid = Array.isArray(row.value)
              ? row.value.length > 0
              : !isNullOrEmpty(row.value);
            const valid = !isNullOrEmpty(row.field) && valueValid;
            if (valid) hasValidRow = true;
            return valid;
          })
      )
      .filter((group) => group.length > 0);

    if (!hasValidRow) {
      Toaster.showWarn("Please fill at least one field.");
      return;
    }

    // Build payload with additional related contacts filters
    const mapValue = (opt) => {
      if (Array.isArray(opt))
        return opt.map((o) =>
          typeof o === "object" ? o.value ?? o.id ?? o : o
        );
      if (opt == null) return [];
      if (typeof opt === "object") return [opt.value ?? opt.id ?? opt];
      return [opt];
    };
    const payload = {
      data: cleaned,
      hasRelatedContacts,
      contactRoleCategory: mapValue(contactRoleCategory),
      contactRoleSegment: mapValue(contactRoleSegment),
      contactRole: mapValue(contactRole),
    };

    console.log("Running report with payload:", payload);
    runReport(payload);
  };

  const onDeleteQueryRow = (key) => {
    console.log("onDeleteQueryRow", key);
    const updated = queryRows
      .map((group) => group.filter((row) => row.key !== key))
      .filter((group) => group.length > 0); // ✅ Remove empty groups
    setQueryRows(updated);
  };

  const onAddRow = (key) => {
    const newRow = initEmptyRow();
    newRow.key = uniqeKey();
    const updated = queryRows.map((group) => {
      const index = group.findIndex((row) => row.key === key);
      if (index !== -1) {
        if (group.length >= 4) {
          Toaster.showWarn("You can only add up to 4 fields per sequence.");
          return group;
        }
        const newGroup = [...group];
        newGroup.splice(index + 1, 0, newRow); // Insert after the matching row
        return newGroup;
      }
      return group;
    });
    setQueryRows(updated);
  };
  const onAddGroup = () => {
    if (queryRows.length >= 3) {
      Toaster.showWarn("You can only add up to 3 sequences.");
      return;
    }
    const newRow = initEmptyRow();
    newRow.key = uniqeKey();
    setQueryRows([...queryRows, [newRow]]); // ✅ Add new group
  };

  // Duplicate the latest sequence (group)
  const onDuplicateGroup = () => {
    if (queryRows.length >= 3) {
      Toaster.showWarn("You can only add up to 3 sequences.");
      return;
    }
    if (queryRows.length === 0) return;
    const lastGroup = queryRows[queryRows.length - 1];
    // Deep copy and assign new keys to each row
    const duplicatedGroup = lastGroup.map((row) => ({
      ...row,
      key: uniqeKey(),
    }));
    setQueryRows([...queryRows, duplicatedGroup]);
  };

  const onSequenceClear = (index) => {
    const updated = [...queryRows];
    updated[index] = [initEmptyRow()];
    setQueryRows(updated);
  };

  const onSequenceDelete = (index) => {
    const updated = queryRows.filter((_, i) => i !== index);
    if (updated.length === 0) {
      setQueryRows([[initEmptyRow()]]);
    } else {
      setQueryRows(updated);
    }
    if (selectedSequenceIndex >= updated.length) {
      setSelectedSequenceIndex(Math.max(0, updated.length - 1));
    }
  };

  const handleAddSequence = () => {
    if (queryRows.length >= 3) {
      Toaster.showWarn("You can only add up to 3 sequences.");
      return;
    }
    const newRow = initEmptyRow();
    newRow.key = uniqeKey();
    const newQueryRows = [...queryRows, [newRow]];
    setQueryRows(newQueryRows);
    setSelectedSequenceIndex(newQueryRows.length - 1);
    setShowConfig(true);
  };

  // clear all rows if change in vendor
  return (
    <>
      {showQuery && (
        <div className="reports-two-panel-layout">
          {!showConfig ? (
            // Show only Sequence List
            <div className="reports-panel reports-panel-full">
              {showSequenceList && (
                <SequenceList
                  sequences={queryRows}
                  vendor={vendor}
                  dropdownData={dropdownData}
                  onSequenceClear={onSequenceClear}
                  onSequenceDelete={onSequenceDelete}
                  onAddSequence={handleAddSequence}
                  onFilterDelete={onDeleteQueryRow}
                  onSequenceEdit={(index) => {
                    setSelectedSequenceIndex(index);
                    setShowConfig(true);
                  }}
                  selectedSequenceIndex={selectedSequenceIndex}
                />
              )}
            </div>
          ) : (
            // Show only Sequence Configuration
            <div className="reports-panel reports-panel-full">
              <SequenceConfig
                sequence={queryRows[selectedSequenceIndex]}
                sequenceIndex={selectedSequenceIndex}
                vendor={vendor}
                onRowChange={onChangeQueryRow}
                onRowDelete={onDeleteQueryRow}
                onRowAdd={onAddRow}
                onClose={() => setShowConfig(false)}
              />
            </div>
          )}
        </div>
      )}

      {/* Action Buttons Section */}
      <div className="reports-generate-section">
        <div className="reports-action-buttons">
          <div className="reports-generate-button">
            <span
              className="reports-generate-text"
              onClick={onRunReport}
              style={{ cursor: "pointer", marginRight: "16px" }}
            >
              Generate Report
            </span>

            <span
              className="reports-generate-text"
              onClick={() => setShowQuery(!showQuery)}
              style={{ cursor: "pointer" }}
            >
              {showQuery ? "Hide" : "Show"} Query
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reports;
